import React, { useState, useEffect } from 'react';
import { X, Upload, ArrowRight, TrendingUp, ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TradeOfferModal = ({ isOpen, onClose, product, selectedVariant }) => {
  const [itemName, setItemName] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [itemValue, setItemValue] = useState('');
  const [cashOffer, setCashOffer] = useState('');
  const [images, setImages] = useState([]);
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [agreed, setAgreed] = useState(false);

  const getToken = () => {
    return localStorage.getItem('blkmrkt_token') || sessionStorage.getItem('blkmrkt_token');
  };

  // Calculate offer strength - only when BOTH values are entered
  const productPrice = selectedVariant?.price ?? product?.price ?? 0;
  const itemValueNum = parseFloat(itemValue) || 0;
  const cashOfferNum = parseFloat(cashOffer) || 0;
  const totalOffer = itemValueNum + cashOfferNum;
  const offerPercentage = productPrice > 0 ? (totalOffer / productPrice) * 100 : 0;
  
  // Only show strength bar when BOTH item value AND cash offer have values
  const showStrengthBar = itemValueNum > 0 && cashOfferNum > 0;

  const getOfferStrength = () => {
    if (offerPercentage < 80) return { label: 'WEAK', color: 'bg-red-500', textColor: 'text-red-400' };
    if (offerPercentage < 100) return { label: 'FAIR', color: 'bg-orange-500', textColor: 'text-orange-400' };
    if (offerPercentage < 120) return { label: 'GOOD', color: 'bg-yellow-500', textColor: 'text-yellow-400' };
    return { label: 'STRONG', color: 'bg-green-500', textColor: 'text-green-400' };
  };

  const offerStrength = getOfferStrength();
  // Cap the visual bar at 120% (strong offer threshold)
  const barWidth = Math.min(100, (offerPercentage / 120) * 100);

  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setItemName('');
      setItemDescription('');
      setItemValue('');
      setCashOffer('');
      setImages([]);
      setNotes('');
      setError('');
      setSuccess('');
    }
  }, [isOpen]);

  const getImageUrl = (img) => {
    if (!img) return '';
    if (img.startsWith('http')) return img;
    return `${API_URL}${img}`;
  };

  const handleImageUpload = async (files) => {
    if (images.length + files.length > 5) {
      setError('MAXIMUM 5 IMAGES ALLOWED');
      return;
    }

    setUploading(true);
    const token = getToken();

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch(`${API_URL}/api/upload/user-image`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (res.ok) {
          const data = await res.json();
          setImages(prev => [...prev, data.url]);
        } else {
          const errData = await res.json().catch(() => ({}));
          setError(errData.detail || 'UPLOAD FAILED');
        }
      } catch (err) {
        console.error('Upload failed:', err);
        setError('UPLOAD FAILED. PLEASE TRY AGAIN.');
      }
    }

    setUploading(false);
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleSubmit = async () => {
    setError('');

    if (!itemName.trim()) {
      setError('PLEASE ENTER YOUR ITEM NAME');
      return;
    }
    if (!itemDescription.trim()) {
      setError('PLEASE DESCRIBE YOUR ITEM');
      return;
    }
    if (images.length === 0) {
      setError('PLEASE UPLOAD AT LEAST ONE PHOTO OF YOUR ITEM');
      return;
    }
    if (!itemValue || parseFloat(itemValue) <= 0) {
      setError('PLEASE ENTER YOUR ITEM\'S ESTIMATED VALUE');
      return;
    }
    if (!cashOffer || parseFloat(cashOffer) < 0) {
      setError('PLEASE ENTER YOUR CASH OFFER AMOUNT');
      return;
    }
    if (!agreed) {
      setError('PLEASE READ AND ACCEPT THE TRADE TERMS');
      return;
    }

    setIsSubmitting(true);
    const token = getToken();

    if (!token) {
      setError('PLEASE LOG IN TO SUBMIT A TRADE OFFER');
      setIsSubmitting(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/submissions/trade-offer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          product_id: product.id,
          variant_id: selectedVariant?.id || null,
          variant_name: selectedVariant?.name || null,
          item_name: itemName,
          item_description: itemDescription,
          item_estimated_value: parseFloat(itemValue),
          cash_offer: parseFloat(cashOffer),
          images: images,
          notes: notes || null
        })
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess('TRADE OFFER SUBMITTED! WE\'LL REVIEW AND GET BACK TO YOU.');
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(data.detail?.toUpperCase() || 'FAILED TO SUBMIT OFFER');
      }
    } catch (err) {
      setError('FAILED TO SUBMIT OFFER. PLEASE TRY AGAIN.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-zinc-900 border-b border-zinc-800 p-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold tracking-wider font-mono">OFFER TRADE</h2>
            <p className="text-xs text-zinc-500 font-mono">FOR {product?.name?.toUpperCase()}</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Product Being Traded For */}
          {(() => {
            // Resolve the variant's cover photo when a variant is selected
            let coverImage = product?.image_url;
            if (selectedVariant) {
              if (selectedVariant.image_url) {
                coverImage = selectedVariant.image_url;
              } else if (selectedVariant.image_indices?.length > 0 && product?.images?.length) {
                const idx = selectedVariant.image_indices[0];
                if (typeof idx === 'number' && idx >= 0 && idx < product.images.length) {
                  coverImage = product.images[idx];
                }
              } else if (typeof selectedVariant.image_index === 'number' && product?.images?.length) {
                const idx = selectedVariant.image_index;
                if (idx >= 0 && idx < product.images.length) {
                  coverImage = product.images[idx];
                }
              }
            }
            return (
              <div className="flex items-center gap-3 bg-zinc-800 rounded-lg p-3">
                {coverImage && (
                  <img 
                    src={getImageUrl(coverImage)}
                    alt={product?.name}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                )}
                <div className="flex-1">
                  <p className="text-sm text-zinc-400 font-mono">YOU WANT</p>
                  <p className="font-medium font-mono uppercase">{product?.name}</p>
                  {selectedVariant?.name && (
                    <p className="text-[11px] text-purple-400 font-mono uppercase mt-0.5">
                      {selectedVariant.name}
                    </p>
                  )}
                  <p className="text-teal-400 font-mono">${(selectedVariant?.price ?? product?.price)?.toFixed(2)}</p>
                </div>
              </div>
            );
          })()}

          {/* Error/Success */}
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 px-3 py-2 rounded-lg text-sm font-mono">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-500/20 border border-green-500 text-green-400 px-3 py-2 rounded-lg text-sm font-mono">
              {success}
            </div>
          )}

          {/* Your Item */}
          <div className="border border-zinc-700 rounded-lg p-4 space-y-3">
            <p className="text-sm text-zinc-400 flex items-center gap-2 font-mono">
              <ArrowRight size={14} />
              YOUR TRADE ITEM
            </p>

            <input
              type="text"
              value={itemName}
              onChange={(e) => setItemName(e.target.value.toUpperCase())}
              placeholder="ITEM NAME..."
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none font-mono uppercase"
            />

            <textarea
              value={itemDescription}
              onChange={(e) => setItemDescription(e.target.value.toUpperCase())}
              placeholder="DESCRIBE CONDITION, ANY FLAWS..."
              rows={2}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none resize-none font-mono uppercase"
            />

            <div>
              <label className="block text-xs text-zinc-500 mb-1 font-mono">ESTIMATED VALUE ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={itemValue}
                onChange={(e) => setItemValue(e.target.value)}
                placeholder="0.00"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none font-mono"
              />
            </div>

            {/* Images - Required */}
            <div>
              <label className="block text-xs text-zinc-500 mb-1 font-mono">PHOTOS OF YOUR ITEM *</label>
              {images.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {images.map((img, idx) => (
                    <div key={idx} className="relative w-16 h-16 rounded-lg overflow-hidden border border-zinc-700">
                      <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={() => removeImage(idx)}
                        className="absolute top-1 right-1 p-0.5 bg-black/70 rounded-full hover:bg-red-500"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {images.length < 5 && (
                <label className={`block border border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors ${images.length === 0 ? 'border-red-500/50 hover:border-red-500' : 'border-zinc-700 hover:border-teal-500'}`}>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => handleImageUpload(Array.from(e.target.files))}
                    className="hidden"
                    disabled={uploading}
                  />
                  {uploading ? (
                    <span className="text-sm text-teal-400 font-mono flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-teal-400 border-t-transparent rounded-full animate-spin"></span>
                      UPLOADING...
                    </span>
                  ) : (
                    <span className={`text-sm flex items-center justify-center gap-2 font-mono ${images.length === 0 ? 'text-red-400' : 'text-zinc-500'}`}>
                      <Upload size={14} /> {images.length === 0 ? 'ADD PHOTOS (REQUIRED)' : 'ADD MORE PHOTOS'}
                    </span>
                  )}
                </label>
              )}
            </div>
          </div>

          {/* Cash Offer */}
          <div>
            <label className="block text-xs text-zinc-500 mb-1 font-mono">+ CASH OFFER ($)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={cashOffer}
              onChange={(e) => setCashOffer(e.target.value)}
              placeholder="0.00"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none font-mono"
            />
          </div>

          {/* Offer Strength Meter - Only show when BOTH values are entered */}
          {showStrengthBar && (
            <div className="bg-zinc-800 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-400 flex items-center gap-2 font-mono">
                  <TrendingUp size={14} />
                  OFFER STRENGTH
                </span>
                <span className={`text-sm font-bold font-mono ${offerStrength.textColor}`}>
                  {offerStrength.label}
                </span>
              </div>

              {/* Progress Bar */}
              <div className="h-3 bg-zinc-700 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${offerStrength.color} transition-all duration-300`}
                  style={{ width: `${barWidth}%` }}
                />
              </div>

              {/* Breakdown */}
              <div className="flex justify-between text-sm font-mono">
                <div>
                  <p className="text-zinc-500">YOUR ITEM</p>
                  <p>${itemValueNum.toFixed(2)}</p>
                </div>
                <div className="text-center">
                  <p className="text-zinc-500">+ CASH</p>
                  <p>${cashOfferNum.toFixed(2)}</p>
                </div>
                <div className="text-right">
                  <p className="text-zinc-500">= TOTAL</p>
                  <p className={`font-bold ${offerStrength.textColor}`}>
                    ${totalOffer.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-xs text-zinc-500 mb-1 font-mono">ADDITIONAL NOTES</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value.toUpperCase())}
              placeholder="ANYTHING ELSE WE SHOULD KNOW..."
              rows={2}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none resize-none font-mono uppercase"
            />
          </div>

          {/* Trade Terms Notice */}
          <div className="bg-zinc-800/50 border border-zinc-700 p-3">
            <p className="text-[10px] text-tiffany-400 font-mono tracking-widest mb-2">TRADE TERMS</p>
            <ul className="text-[10px] text-zinc-400 font-mono leading-relaxed space-y-1 list-disc pl-4">
              <li>YOUR ITEM MUST BE RECEIVED AND VERIFIED BEFORE WE SHIP YOURS.</li>
              <li>ALL TRADES ARE SUBJECT TO INSPECTION AND APPROVAL.</li>
              <li>IF YOUR ITEM IS NOT AS DESCRIBED, WE MAY DECLINE THE TRADE — RETURN SHIPPING IS AT YOUR COST.</li>
              <li>CASH OFFERS ARE PAYABLE VIA PAYPAL ONCE THE TRADE IS ACCEPTED.</li>
            </ul>
          </div>

          {/* Agreement checkbox */}
          <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="trade-agree-label">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-teal-500 cursor-pointer shrink-0"
              data-testid="trade-agree-checkbox"
            />
            <span className="text-[11px] font-mono text-zinc-300 leading-relaxed">
              I HAVE READ AND ACCEPT THE TRADE TERMS ABOVE.
            </span>
          </label>

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || uploading || success}
            className="w-full py-3 bg-teal-500 hover:bg-teal-600 disabled:bg-zinc-700 disabled:text-zinc-500 text-black font-bold rounded-lg transition-colors font-mono flex items-center justify-center gap-2"
          >
            {(uploading || isSubmitting) && (
              <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
            )}
            {uploading ? 'UPLOADING PHOTOS...' : isSubmitting ? 'SUBMITTING...' : 'SUBMIT TRADE OFFER'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TradeOfferModal;
